import styles from './sales-offer-summary.module.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import WebkuaTable from '../../webkua-table/webkua-table';
import { IndicatorEnum, IProductGroup, ISalesProposal } from '../../_models/product-group';
import {
  Box, calculateOneTimeDiscountFromPrice, calculatePriceToRenewal, calculatePriceToRenewalTotal, calculateRecurringDiscountFromPrice, calculateRecurringPrice, calculateRecurringPriceTotal, Checkbox, CheckboxLabel, CreateHeaders, currencyHeader, Dialog, DialogActions, DialogContent, DialogTitle, Divider, ErrorButton, GrayOutlinedButton, HeaderSizeEnum, Icon, LoadingButton, NumberSelect,
  PrimaryButton, Roles, SecondaryButton, SuccessButton, TextField, textHeader, toCurrency, Typography
} from '@frontend-workspace/react-shared-lib';
import { customerPriceToRenewalRenderer, customerRecurringPriceRenderer, DiscountPercentRenderer, OneTimeDiscountRenderer, PriceToRenewalRenderer, RecurringPriceRenderer } from '../sales-offer-table-renderers/sales-offer-table-renderers';
import SalesOfferDetails from './sales-offer-details/sales-offer-details';
import { ValueFormatterParams } from 'ag-grid-community';
import { SubmitOfferWarningDialog, useSalesOfferContext } from '@frontend-workspace/webkua-lib';
import { ProposalStateEnum } from '../../_enums/proposal-state';
import { GetRoles } from '../../TokenContext';

/* eslint-disable-next-line */
export interface SalesOfferSummaryProps {
  orderNum: string,
  transactionInProgress: boolean,
  onSetOrderNum: (orderNum: string) => void,
  onSetCurrentProductGroup: (currentProductGroup: IProductGroup[]) => void,
  onSaveOffer: () => void,
  onSubmitOffer: () => void,
  onAcceptCounterProposal: () => void,
  onDeleteProposal: () => void
  onCancel: () => void,
  onShowProductSelection: () => void,
  data: ISalesProposal
}

export function SalesOfferSummary(props: SalesOfferSummaryProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formData, updateFormData } = useSalesOfferContext();
  const userRole = GetRoles();
  const [cartWithoutZeroes, setCartWithoutZeroes] = useState<IProductGroup[]>([]);
  const [confirmationVisible, setConfirmationVisible] = useState<boolean>(false);
  const [orderId, setOrderId] = useState<string>('');
  const [discountPercent, setDiscountPercent] = useState<number>(0);

  const [oneTimeDiscountTotal, setoneTimeDiscountTotal] = useState<number>(0);
  const [recurringDiscountTotal, setRecurringDiscountTotal] = useState<number>(0);

  const [priceToRenewalTotal, setPriceToRenewalTotal] = useState<number>(0);
  const [recurringPriceTotal, setRecurringPriceTotal] = useState<number>(0);
  const [SubmitOfferWarningDialogOpen, setSubmitOfferWarningDialogOpen] = useState<boolean>(false);

  const [deleteOfferDialogOpen, setDeleteOfferDialogOpen] = useState(false);

  const navigateToHome = () => { navigate(`/`) };
  const navigateToProducts = () => { navigate(`/products`) };

  useEffect(() => {
    const filteredCart = formData.cart.filter(p => p.newSeats > 0);
    setCartWithoutZeroes(filteredCart);
  }, [formData.cart]);

  useEffect(() => {
    let oneTimeDiscountTotal = 0;
    let recurringDiscountTotal = 0;
    let priceToRenewalTotal = 0;
    let recurringPriceTotal = 0;
    formData.cart.forEach(product => {
      priceToRenewalTotal += calculatePriceToRenewal(product.oneTimeDiscountAmount, product.priceToRenewal, product.newSeats);
      recurringPriceTotal += calculateRecurringPrice(product.recurringDiscountPercent, product.recurringPrice, product.newSeats);
    });
    oneTimeDiscountTotal = calculatePriceToRenewalTotal(formData.cart) - priceToRenewalTotal;
    recurringDiscountTotal = calculateRecurringPriceTotal(formData.cart) - recurringPriceTotal;

    setPriceToRenewalTotal(Math.max(priceToRenewalTotal, 0));
    setRecurringPriceTotal(Math.max(recurringPriceTotal, 0));
    setoneTimeDiscountTotal(Math.max(oneTimeDiscountTotal, 0));
    setRecurringDiscountTotal(Math.max(recurringDiscountTotal, 0));
  }, [formData.cart]);

  const removeProduct = (e: IProductGroup) => {
    if (cartWithoutZeroes.length === 1) {
      updateFormData('cart', []);
      updateFormData('contract', null);
      props.onSetCurrentProductGroup([]);
      props.onSetOrderNum('');
    } else {
      const updatedCart = formData.cart.map(product => {
        if (product.itemInfoId === e.itemInfoId) {
          return { ...product, newSeats: 0, indicators: [] };
        }
        return { ...product, indicators: [] };
      });
      updateFormData('cart', updatedCart);
    }
  };

  const seatsCellRenderer = (cellprops: any, params?: any) => {
    return (
      <NumberSelect
        value={cellprops.value}
        onValueChange={(e) => {
          const updatedSeats = formData.cart.map(product => {
            if (product.itemInfoId === cellprops.data.itemInfoId) {
              return { ...product, newSeats: e < 1 ? 1 : e, indicators: [IndicatorEnum.PriceToRenewal, IndicatorEnum.RecurringPrice] };
            }
            return { ...product, indicators: [] };
          });
          updateFormData('cart', updatedSeats);

        }}
        supportNegative={false}
      />
    )
  };

  const freeSeatAlterationRenderer = (params: ValueFormatterParams) => {
    const handleOnClick = (event: React.ChangeEvent<Element>, checked: boolean) => {
      const updatedCart = formData.cart.map((item: IProductGroup) =>
        item.itemInfoId === params.data.itemInfoId ? { ...item, allowCustomerSeatAlteration: checked, indicators: [] } : { ...item, indicators: [] }
      );
      updateFormData('cart', updatedCart);
    }
    return <Checkbox
      checked={params.value}
      onChange={handleOnClick}
    />
  };

  const handleClose = (event: any, reason: any) => {
    if (reason !== 'backdropClick') {
      setConfirmationVisible(false);
    }
  };

  const secondaryButtonCellRenderer = (props: any) => (
    <SecondaryButton
      variant={props.variant || "outlined"}
      size='small'
      disabled={props.enabledField ? !props.data[props.enabledField] : false}
      onClick={() => { props.onClick(props.data) }}>
      {props.buttonText}
    </SecondaryButton>
  );

  const updateRecurringDiscount = (recurringDiscount: number, itemInfoId: string) => {
    const updatedCart = formData.cart.map((item: IProductGroup) => {
      if (item.itemInfoId === itemInfoId) {
        const indicators = recurringDiscount > item.oneTimeDiscountAmount ? [IndicatorEnum.OneTimeDiscount, IndicatorEnum.RecurringPrice, IndicatorEnum.PriceToRenewal] : [IndicatorEnum.RecurringPrice];
        return { ...item, recurringDiscountPercent: recurringDiscount, oneTimeDiscountAmount: Math.max(recurringDiscount, item.oneTimeDiscountAmount), indicators: indicators };
      }
      return { ...item, indicators: [] };
    }
    );
    updateFormData('cart', updatedCart);
  }

  const updateOneTimeDiscount = (oneTimeDiscount: number, itemInfoId: string) => {
    const updatedCart = formData.cart.map((item: IProductGroup) => {
      if (item.itemInfoId === itemInfoId) {
        const indicators = oneTimeDiscount < item.recurringDiscountPercent ? [IndicatorEnum.RecurringDiscount, IndicatorEnum.RecurringPrice, IndicatorEnum.PriceToRenewal] : [IndicatorEnum.PriceToRenewal];
        return { ...item, oneTimeDiscountAmount: oneTimeDiscount, recurringDiscountPercent: Math.min(oneTimeDiscount, item.recurringDiscountPercent), indicators: indicators };
      }
      return { ...item, indicators: [] };
    }
    );
    updateFormData('cart', updatedCart);
  }

  const updateRecurringPrice = (newPrice: number, itemInfoId: string) => {
    const updatedCart = formData.cart.map((item: IProductGroup) => {
      if (item.itemInfoId === itemInfoId) {
        const recurringDiscount = calculateRecurringDiscountFromPrice(newPrice, item);
        const indicators = recurringDiscount > item.oneTimeDiscountAmount ? [IndicatorEnum.RecurringDiscount, IndicatorEnum.OneTimeDiscount, IndicatorEnum.PriceToRenewal] : [IndicatorEnum.RecurringDiscount];
        return { ...item, recurringDiscountPercent: recurringDiscount, oneTimeDiscountAmount: Math.max(recurringDiscount, item.oneTimeDiscountAmount), indicators: indicators };
      }
      return { ...item, indicators: [] };
    });
    updateFormData('cart', updatedCart);
  }

  const updatePriceToRenewal = (newPrice: number, itemInfoId: string) => {
    const updatedCart = formData.cart.map((item: IProductGroup) => {
      if (item.itemInfoId === itemInfoId) {
        const oneTimeDiscountAmount = calculateOneTimeDiscountFromPrice(newPrice, item);
        const indicators = oneTimeDiscountAmount < item.recurringDiscountPercent ? [IndicatorEnum.RecurringDiscount, IndicatorEnum.OneTimeDiscount, IndicatorEnum.RecurringPrice] : [IndicatorEnum.OneTimeDiscount];
        return { ...item, oneTimeDiscountAmount: oneTimeDiscountAmount, recurringDiscountPercent: Math.min(oneTimeDiscountAmount, item.recurringDiscountPercent), indicators: indicators };
      }
      return { ...item, indicators: [] };
    });
    updateFormData('cart', updatedCart);
  };

  const onSubmitOffer = () => {
    props.onSubmitOffer();
    setSubmitOfferWarningDialogOpen(false);
  };

  const onAcceptCounterProposal = () => {
    props.onAcceptCounterProposal();
  };

  const ConfirmationDialog = () => {
    return <Dialog
      open={confirmationVisible}
      onClose={handleClose}
      maxWidth='sm'>
      <DialogTitle className='mb-4'>
        {''}
      </DialogTitle>
      <DialogContent>
        <div style={{ textAlign: 'center' }}>
          <Icon
            color='success'
            fontSize='large'
            className="material-icons-outlined">
            check_circle_outline
          </Icon>
          <Typography
            variant="h3"
            component="div"
            className="mb-2">
            {t('shop.orderCompleted')}
          </Typography>
          <Typography
            variant="h4"
            component="div"
            className="mb-4">
            {t('shop.thankYouForPurchase')}
          </Typography>
        </div>
        <Typography
          variant="body1"
          component="div">
          {t("shop.orderCompleted2", { orderId: orderId })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="outlined"
          size='large'
          onClick={navigateToHome}
          className='minButtonWidth'>
          {t("standard.frontPage")}
        </PrimaryButton>
        <Divider orientation="vertical" variant="middle" flexItem className="mx-4" />
        <SuccessButton
          variant="contained"
          size='large'
          onClick={(e) => props.onCancel()} //TODO does nothing
          className='minButtonWidth'>
          {t("standard.newPurchase")}
        </SuccessButton>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={navigateToProducts}>
          {t("standard.manageAccesses")}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  };

  const updateDiscount = () => {
    const updatedDiscount = formData.cart.map(product => {
      return { ...product, recurringDiscountPercent: discountPercent, oneTimeDiscountAmount: discountPercent, indicators: [IndicatorEnum.RecurringDiscount, IndicatorEnum.OneTimeDiscount, IndicatorEnum.RecurringPrice, IndicatorEnum.PriceToRenewal] };
    });
    updateFormData('cart', updatedDiscount);
  }

  const cartHasDiscountErrors = () => {
    return formData.cart.some(item => {
      if (item.newSeats === 0) {
        return false;
      } else {
        return item.recurringDiscountPercent !== 0;
      }
    });
  };

  return <>
    <Box
      component="div"
      className="pb-4 pb-xl-0 mb-4">
      <SalesOfferDetails />
      <Typography variant='h5' className='mt-4'>{t('standard.ourProposal')}</Typography>
      <WebkuaTable
        headers={CreateHeaders([
          textHeader("newSeats", t("shop.addSeats"), HeaderSizeEnum.MEDIUM, {
            renderer: seatsCellRenderer
          }),
          textHeader("allowCustomerSeatAlteration", t("shop.freeSeatAlteration"), HeaderSizeEnum.MEDIUM, {
            renderer: freeSeatAlterationRenderer
          }),
          textHeader("name", t("shop.productName"), HeaderSizeEnum.LARGE),
          textHeader("licenseType", t("shop.type"), HeaderSizeEnum.MEDIUM, {
            renderer: (params: any) => { return t(`licenseType.${params.value?.toLowerCase()}`) }
          }),
          textHeader("licenseNumber", t("shop.licenseNumber"), HeaderSizeEnum.MEDIUM),
          currencyHeader("recurringPrice", t("shop.pricePerSeat"), HeaderSizeEnum.MEDIUM, {
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
          textHeader("recurringDiscountPercent", t("standard.recurringDiscount"), HeaderSizeEnum.LARGE, {
            renderer: DiscountPercentRenderer,
            rendererParams: {
              onBlur: (discountPercent: number, itemInfoId: string) => {
                updateRecurringDiscount(discountPercent, itemInfoId)
              }
            },
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
          textHeader("recurringPrice", t("shop.sumSubscription"), HeaderSizeEnum.LARGE, {
            renderer: RecurringPriceRenderer,
            rendererParams: {
              onBlur: (recurringPrice: number, itemInfoId: string) => {
                updateRecurringPrice(recurringPrice, itemInfoId)
              }
            },
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
          textHeader("oneTimeDiscountAmount", t("standard.oneTimeDiscount"), HeaderSizeEnum.LARGE, {
            renderer: OneTimeDiscountRenderer,
            rendererParams: {
              onBlur: (discountPercent: number, itemInfoId: string) => {
                updateOneTimeDiscount(discountPercent, itemInfoId)
              }
            },
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
          textHeader("priceToRenewal", t("shop.sumUntilRenewal"), HeaderSizeEnum.LARGE, {
            renderer: PriceToRenewalRenderer,
            rendererParams: {
              onBlur: (priceToRenewal: number, itemInfoId: string) => {
                updatePriceToRenewal(priceToRenewal, itemInfoId)
              }
            },
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
          textHeader("", t(""), HeaderSizeEnum.MEDIUM, {
            renderer: secondaryButtonCellRenderer,
            rendererParams: {
              buttonText: t("standard.remove"),
              onClick: (e: IProductGroup) => {
                removeProduct(e);
              }
            },
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
        ])}
        data={cartWithoutZeroes}
        pageSize={999}
        disableSearch={true}
        hidePagination={true}
      />
      {formData.customerCart.length > 0 &&
        <>
          <Typography variant='h5' className='mt-4'>{t('shop.customerProposal')}</Typography>
          <WebkuaTable
            headers={CreateHeaders([
              textHeader("newSeats", t("shop.seats"), HeaderSizeEnum.MEDIUM),
              textHeader("name", t("shop.productName"), HeaderSizeEnum.LARGE),
              textHeader("licenseType", t("shop.type"), HeaderSizeEnum.MEDIUM, {
                renderer: (params: any) => { return t(`licenseType.${params.value?.toLowerCase()}`) }
              }),
              textHeader("licenseNumber", t("shop.licenseNumber"), HeaderSizeEnum.MEDIUM),
              currencyHeader("recurringPrice", t("shop.pricePerSeat"), HeaderSizeEnum.MEDIUM, {
                cellStyle: { justifyContent: 'right' },
                headerClass: "ag-right-aligned-header"
              }),
              textHeader("recurringDiscountPercent", t("standard.recurringDiscount"), HeaderSizeEnum.MEDIUM, {
                cellStyle: { justifyContent: 'right' },
                headerClass: "ag-right-aligned-header"
              }),
              textHeader("oneTimeDiscountAmount", t("standard.oneTimeDiscount"), HeaderSizeEnum.MEDIUM, {
                cellStyle: { justifyContent: 'right' },
                headerClass: "ag-right-aligned-header"
              }),
              currencyHeader("recurringPrice", t("shop.sumSubscription"), HeaderSizeEnum.LARGE, {
                renderer: customerRecurringPriceRenderer,
                cellStyle: { justifyContent: 'right' },
                headerClass: "ag-right-aligned-header"
              }),
              currencyHeader("priceToRenewal", t("shop.sumUntilRenewal"), HeaderSizeEnum.LARGE, {
                renderer: customerPriceToRenewalRenderer,
                cellStyle: { justifyContent: 'right' },
                headerClass: "ag-right-aligned-header"
              })
            ])}
            data={formData.customerCart}
            pageSize={999}
            disableSearch={true}
            hidePagination={true}
          />
        </>}
      <Row>
        <Col sm={12} xl={8}>
          <div className="d-flex flex-row align-items-end mt-3">
            <PrimaryButton
              variant="contained"
              disabled={formData.state === ProposalStateEnum.ACCEPTED}
              onClick={props.onShowProductSelection}
              className="minButtonWidth">
              <Icon className="me-1" fontSize="small">add</Icon><span className='text-truncate'>{t("standard.addProduct")}</span>
            </PrimaryButton>
            <Divider orientation="vertical" variant="fullWidth" flexItem className="align-self-end mx-1 mx-lg-4" sx={{ height: '32px !important' }} />
            <div className='d-flex flex-row flex-wrap align-items-end'>
              <div className='me-1 me-sm-2'>
                <TextField
                  type='number'
                  label={t('standard.discount')}
                  name='discount'
                  sx={{ width: '100px' }}
                  value={discountPercent || 0}
                  onChange={(e) => setDiscountPercent(e.target.valueAsNumber)}
                  onKeyDown={(e) => e.key === 'Enter' && updateDiscount()}
                />
              </div>
              <div className='mt-1 mt-sm-0'>
                <PrimaryButton
                  variant="outlined"
                  onClick={updateDiscount}
                  className='minButtonWidth'>
                  <Icon className="me-1" fontSize="small">percent</Icon>{t("standard.setDiscountOnAll")}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </Col>
        <Col sm={12} xl={4} className='d-flex flex-column align-self-end mt-3'>
          <div className='d-flex justify-content-end'>
            <Typography
              className='me-2'
              component="div"
              variant="body6">
              {t('shop.oneTimeDiscountTotal')}:
            </Typography>
            <Typography
              component="div"
              variant="body6">
              {toCurrency(oneTimeDiscountTotal) || 0}
            </Typography>
          </div>
          <div className='d-flex justify-content-end mb-3'>
            <Typography
              className='me-2'
              component="div"
              variant="body6">
              {t('shop.recurringDiscountTotal')}:
            </Typography>
            <Typography
              component="div"
              variant="body6">
              {toCurrency(recurringDiscountTotal) || 0}
            </Typography>
          </div>
          <div className='d-flex justify-content-end'>
            <Typography
              className='me-2'
              component="div"
              variant="body6">
              {t('shop.recurringTotalPrice')}:
            </Typography>
            <Typography
              component="div"
              variant="body6">
              {toCurrency(recurringPriceTotal) || 0}
            </Typography>
          </div>
          <div className='d-flex justify-content-end'>
            <Typography
              className='me-2'
              component="div"
              variant="body6">
              {t('shop.priceNowMsg')}:
            </Typography>
            <Typography
              component="div"
              variant="body6">
              {toCurrency(priceToRenewalTotal) || 0}
            </Typography>
          </div>
        </Col>
      </Row>
      {/* <Row>
        <Col className="d-flex flex-wrap mt-3 justify-content-end align-items-center">
          {formData.state === ProposalStateEnum.COUNTER_PROPOSAL && <LoadingButton
            loading={props.transactionInProgress}
            color="success"
            variant="outlined"
            disabled={props.transactionInProgress}
            onClick={onAcceptCounterProposal}
            className="minButtonWidth my-2"
            loadingPosition="start"
            startIcon={<Icon>check</Icon>}>
            {t("shop.acceptCounterProposal")}
          </LoadingButton>}
          {formData.state === ProposalStateEnum.COUNTER_PROPOSAL && <Divider orientation="vertical" variant="middle" flexItem className="mx-1 mx-xl-4" />}
          <LoadingButton
            loading={props.transactionInProgress}
            color="success"
            variant="contained"
            disabled={!cartWithoutZeroes.length || props.transactionInProgress || formData.state === ProposalStateEnum.ACCEPTED}
            onClick={() => setSubmitOfferWarningDialogOpen(true)}
            className="minButtonWidth me-3 my-2"
            loadingPosition="end"
            endIcon={<Icon>send</Icon>}>
            {t("shop.sendOffer")}
          </LoadingButton>
          <PrimaryButton
            color="success"
            variant="contained"
            disabled={props.transactionInProgress || formData.state === ProposalStateEnum.ACCEPTED}
            onClick={() => props.onSaveOffer()}
            className="minButtonWidth my-2"
            startIcon={<Icon>save</Icon>}>
            {t("shop.saveOffer")}
          </PrimaryButton>
          <Divider orientation="vertical" variant="middle" flexItem className="mx-1 mx-xl-4" />
          <ErrorButton
            variant="contained"
            disabled={props.transactionInProgress || formData.state === ProposalStateEnum.ACCEPTED}
            onClick={() => setDeleteOfferDialogOpen(true)}
            className="minButtonWidth my-2">
            <Icon className='me-1' fontSize="small">close</Icon>{t("standard.deleteOffer")}
          </ErrorButton>
        </Col>
      </Row> */}
    </Box>

    <SubmitOfferWarningDialog
      open={SubmitOfferWarningDialogOpen}
      state={formData.state}
      hasError={!userRole.includes(Roles.Admin) && cartHasDiscountErrors()}
      onSubmitOffer={onSubmitOffer}
      onClose={() => setSubmitOfferWarningDialogOpen(false)} />

    <ConfirmationDialog />

    <DeleteOfferDialog
      open={deleteOfferDialogOpen}
      onClose={() => setDeleteOfferDialogOpen(false)}
      onDeleteProposal={props.onDeleteProposal}
      data={props.data}
    />
  </>;
};

function DeleteOfferDialog(props: {
  open: boolean,
  onClose: () => void,
  onDeleteProposal: () => void,
  data: ISalesProposal
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth='sm'
      fullWidth>
      <DialogTitle className='mb-3'>
        {t("standard.areYouSureYouWantToDelete")}
      </DialogTitle>
      <DialogContent>
        <Typography
          component="div"
          variant='subtitle3'>
          {props.data.companyName} - {props.data.proposalNumber}
        </Typography>
      </DialogContent>
      <DialogActions>
        <ErrorButton
          variant="contained"
          size='large'
          onClick={props.onDeleteProposal}
          className='minButtonWidth'>
          {t("standard.deleteOffer")}
        </ErrorButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
};

export default SalesOfferSummary;
